// Sesión del huésped. El token vive en sessionStorage (muere al cerrar la
// pestaña); los borradores del formulario (LocalPaxData_*, CIDSignature_*)
// siguen en localStorage pero se limpian al terminar o cerrar sesión.
const TOKEN_KEY = "token";
const DRAFT_PREFIXES = ["LocalPaxData_", "CIDSignature_"];

const safe = fn => {
  try {
    return fn();
  } catch (e) {
    return null;
  }
};

// Migración de una sola vez: versiones anteriores guardaban el token en
// localStorage. Si hay uno, se mueve a sessionStorage y se borra de ahí.
export const migrateLegacyToken = () => {
  safe(() => {
    const legacy = localStorage.getItem(TOKEN_KEY);
    if (legacy === null) return;
    if (!sessionStorage.getItem(TOKEN_KEY))
      sessionStorage.setItem(TOKEN_KEY, legacy);
    localStorage.removeItem(TOKEN_KEY);
  });
};

export const getToken = () => {
  migrateLegacyToken();
  return safe(() => sessionStorage.getItem(TOKEN_KEY));
};

export const setToken = token => {
  safe(() => sessionStorage.setItem(TOKEN_KEY, token));
  safe(() => localStorage.removeItem(TOKEN_KEY));
};

export const clearToken = () => {
  safe(() => sessionStorage.removeItem(TOKEN_KEY));
  safe(() => localStorage.removeItem(TOKEN_KEY));
};

// UserData ({CID, LastName, CompanyID}) vive en sessionStorage, igual que el
// token. Migración única desde localStorage (versiones anteriores).
const USER_KEY = "UserData";
const NOTICE_KEY = "pxPendingNotice";

export const migrateLegacyUserData = () => {
  safe(() => {
    const legacy = localStorage.getItem(USER_KEY);
    if (legacy === null) return;
    if (!sessionStorage.getItem(USER_KEY))
      sessionStorage.setItem(USER_KEY, legacy);
    localStorage.removeItem(USER_KEY);
  });
};

export const getStoredUserData = () => {
  migrateLegacyUserData();
  return safe(() => JSON.parse(sessionStorage.getItem(USER_KEY)));
};

export const setStoredUserData = userData => {
  safe(() => sessionStorage.setItem(USER_KEY, JSON.stringify(userData)));
  safe(() => localStorage.removeItem(USER_KEY));
};

export const clearUserData = () => {
  safe(() => sessionStorage.removeItem(USER_KEY));
  safe(() => localStorage.removeItem(USER_KEY));
};

// Aviso para la pantalla de login que sobrevive a una navegación completa
// (sesión vencida, cierre de sesión).
export const setPendingNotice = notice => {
  safe(() => sessionStorage.setItem(NOTICE_KEY, JSON.stringify(notice)));
};

export const takePendingNotice = () =>
  safe(() => {
    const raw = sessionStorage.getItem(NOTICE_KEY);
    if (raw === null) return null;
    sessionStorage.removeItem(NOTICE_KEY);
    return JSON.parse(raw);
  });

const DRAFT_PREFIXES_ALL = DRAFT_PREFIXES;
const CACHE_PREFIXES = ["CIDData_", "MenuData_"];
const isDraftKey = key => DRAFT_PREFIXES_ALL.some(p => key.startsWith(p));

// Borra los borradores de una reserva (al terminar el check-in).
export const clearDrafts = cid => {
  safe(() => {
    DRAFT_PREFIXES.forEach(p => localStorage.removeItem(`${p}${cid}`));
  });
};

// Datos personales que quedan en localStorage: caché de la reserva y del menú
// (CIDData_*, MenuData_*) y borradores de otras reservas. Con `keepCid` se
// conservan los borradores de esa reserva.
export const clearPII = keepCid => {
  safe(() => {
    const keys = [];
    for (let i = 0; i < localStorage.length; i++) keys.push(localStorage.key(i));
    keys.forEach(key => {
      if (!key) return;
      if (CACHE_PREFIXES.some(p => key.startsWith(p))) {
        localStorage.removeItem(key);
        return;
      }
      if (!isDraftKey(key)) return;
      if (keepCid && key.split("_")[1] === String(keepCid)) return;
      localStorage.removeItem(key);
    });
  });
};

// Cierra la sesión local: token, UserData, caché y borradores. Con `keepCid`
// se conservan los borradores de esa reserva, nunca los de otras.
export const clearSession = keepCid => {
  clearToken();
  clearUserData();
  clearPII(keepCid);
};

// Migración única al arrancar: token y UserData a sessionStorage, y limpieza
// de caché/borradores ajenos a la sesión actual.
export const bootMigration = () => {
  migrateLegacyToken();
  migrateLegacyUserData();
  const user = getStoredUserData();
  clearPII(user && user.CID);
};
